import { QNotFound, QSpinner, useCurrentUser } from '@qualio/ui-components';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDocTitle, useUsers } from '../../../hooks';
import { filterByPermissionButInclude } from '../../../utils/userUtils';
import EventTemplateForm from './components/EventTemplateForm';
import { useTemplateQuery } from './hooks';

const EventTemplate: React.FC = () => {
  const { companyId } = useCurrentUser();
  const { eventTemplateId } = useParams();
  const templateQuery = useTemplateQuery(companyId, eventTemplateId);
  const { data: usersData, isLoading: isUsersDataLoading } = useUsers();

  useDocTitle(`${templateQuery.data?.eventTemplateFormValues.name} - Event templates - Qualio`);

  const eventTemplateFormValues = useMemo(
    () => templateQuery.data?.eventTemplateFormValues,
    [templateQuery.data?.eventTemplateFormValues],
  );

  const templateIsLoading = templateQuery.isLoading || !templateQuery.isFetchedAfterMount;

  // The template couldn't be fetched (e.g. a 404 for a deleted / non-existent template, or another
  // fetch failure after retries). Show the not-found page instead of spinning forever.
  if (templateQuery.isError && !templateQuery.data) {
    return <QNotFound data-cy="event-template-not-found" />;
  }

  if (templateIsLoading || !usersData || isUsersDataLoading) {
    return <QSpinner />;
  }

  const possibleOwners = filterByPermissionButInclude(
    usersData,
    'can_work_on_issue',
    eventTemplateFormValues!.default_owner_id,
  );

  return (
    <EventTemplateForm
      initialValues={eventTemplateFormValues!}
      possibleOwners={possibleOwners}
      companyId={companyId}
      templateId={Number(eventTemplateId)}
      defaultOwnerHasPermission={templateQuery.data?.defaultOwnerHasPermission}
      ownerId={templateQuery.data?.ownerId}
      ownerName={templateQuery.data?.ownerName}
    />
  );
};

export default EventTemplate;
